import "@/styles/globals.css";

import { Auth0Provider } from "@auth0/auth0-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { SandboxAgentAuthProvider } from "@/components/sandbox-agent-auth-provider";
import { registerSuperProperties, track } from "@/lib/analytics";
import { config } from "@/lib/config";
import { queryClient } from "@/lib/query-client";
import { readSandboxAgentToken } from "@/lib/sandbox-agent";
import { initSentry } from "@/services/sentry";

import { routeTree } from "./routeTree.gen";

// Local sandboxes: an AI agent's token, read (and dropped from the URL) before the router sees the URL.
const sandboxAgentToken = readSandboxAgentToken();

// No scrollRestoration: navigation must never auto-scroll (user requirement) —
// InstantSearch URL writes and search-param panes would each jump to top.
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

// Page views: only real path changes count (InstantSearch and pane state write
// search params constantly), plus the initial resolve exactly once. `route` is
// the matched route id (params stay tokens — low cardinality, use it for
// breakdowns, never `path`). `currentRoute` rides on all events as the feature
// dimension of "Action Performed".
let initialPageViewTracked = false;
router.subscribe("onResolved", (event) => {
  if (initialPageViewTracked && !event.pathChanged) {
    return;
  }
  initialPageViewTracked = true;
  const routeId = router.state.matches.at(-1)?.routeId ?? event.toLocation.pathname;
  registerSuperProperties({ currentRoute: routeId });
  track("Page Viewed", { route: routeId, path: event.toLocation.pathname });
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

initSentry(router);

if (import.meta.env.DEV) {
  void import("@/dev/report-client-errors").then(({ installClientErrorReporter }) => {
    installClientErrorReporter();
  });
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found");
}

const app = (
  <QueryClientProvider client={queryClient}>
    <RouterProvider router={router} />
  </QueryClientProvider>
);

createRoot(rootElement).render(
  <StrictMode>
    {sandboxAgentToken ? (
      <SandboxAgentAuthProvider token={sandboxAgentToken}>{app}</SandboxAgentAuthProvider>
    ) : (
      <Auth0Provider
        domain={config.AUTH0_DOMAIN}
        clientId={config.AUTH0_CLIENT_ID}
        // Refresh tokens + persistent cache: without them every reload restarts
        // interactive auth, and Auth0 re-prompts consent on localhost (consent
        // for the Management-API audience can't be skipped on unverifiable
        // localhost origins).
        useRefreshTokens
        cacheLocation="localstorage"
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: config.AUTH0_AUDIENCE,
        }}
        onRedirectCallback={(appState) => {
          void router.navigate({ to: appState?.returnTo ?? "/", replace: true });
        }}
      >
        {app}
      </Auth0Provider>
    )}
  </StrictMode>,
);
